import React, { useEffect, useRef, useState } from "react";
import { collection, doc, onSnapshot, addDoc, query, orderBy, getDocs, collectionGroup, updateDoc } from "firebase/firestore";
import { Download, Clock, Calendar, ChevronRight, Loader2, MessageCircle, Paperclip, Send, File, CheckCircle } from "lucide-react";
import { auth, db, storage } from "../../firebase";
import { useParams } from "react-router-dom";
import QRCode from "react-qr-code";
import moment from "moment";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import html2canvas from "html2canvas";
import toast from "react-hot-toast";
import Homes from "../../components/Home";

const ViewVisit = () => {
    const qrRef = useRef(null);

    const { visitId } = useParams();
    const [booking, setBooking] = useState(null);
    const [loading, setLoading] = useState(true);
    const [chatOpen, setChatOpen] = useState(false);
    const [message, setMessage] = useState("");
    const [messages, setMessages] = useState([]);
    const [file, setFile] = useState(null);
    const [confirmed, setConfirmed] = useState(false);

    const userId = auth?.currentUser?.uid;

    const formattedDate = booking?.assignedData
        ? moment(booking?.assignedData?.date?.seconds * 1000).format("MMMM D, YYYY")
        : "No Date";
    const formattedTime = booking?.assignedData
        ? moment(booking?.assignedData?.time?.seconds * 1000).format("h:mm A")
        : "";

    useEffect(() => {
        if (!visitId) return;
    
        let unsubBooking = () => {};
        let unsubMessages = () => {};
    
        const fetchBooking = async () => {
            setLoading(true);
            try {
                const bookingsQuery = collectionGroup(db, "bookings");
                const querySnapshot = await getDocs(bookingsQuery);
                const foundDoc = querySnapshot.docs.find((doc) => doc.id === visitId);
    
                if (foundDoc) {
                    // Subscribe to booking updates
                    unsubBooking = onSnapshot(foundDoc.ref, (docSnap) => {
                        if (docSnap.exists()) {
                            setBooking({ id: docSnap.id, ...docSnap.data() });
                        } else {
                            setBooking(null);
                        }
                    });
    
                    // Subscribe to messages updates
                    const messagesRef = collection(foundDoc.ref, "messages");
                    const messagesQuery = query(messagesRef, orderBy("timestamp", "asc"));
                    unsubMessages = onSnapshot(messagesQuery, (querySnapshot) => {
                        setMessages(querySnapshot.docs.map(doc => doc.data()));
                    });
                } else {
                    setBooking(null);
                    setMessages([]);
                }
            } catch (error) {
                console.error("Error fetching booking and messages:", error);
            } finally {
                setLoading(false);
            }
        };
    
        fetchBooking();
    
        return () => {
            unsubBooking();
            unsubMessages();
        };
    }, [visitId]);
    
    const confirmAttendance = async () => {
        if (!visitId || !userId) return;
        try {
            toast.success("This confirm's that you'll surely attend the visit")
            const bookingRef = doc(db, "members", userId, "bookings", visitId);
            await updateDoc(bookingRef, { confirmed: true });
            setConfirmed(true);
        } catch (error) {
            console.log(error)
            toast.error("Error confirming attendance");
        }
    };

    // Send Message
    const sendMessage = async () => {
        if (!message.trim() && !file) return;

        let fileURL = '';
        if (file) {
            const storageRef = ref(storage, `messages/${userId}/${visitId}/${file.name}`);
            const snapshot = await uploadBytes(storageRef, file);
            fileURL = await getDownloadURL(snapshot.ref);
        }

        const messagesRef = collection(db, 'members', userId, 'bookings', visitId, 'messages');
        await addDoc(messagesRef, {
            sender: auth.currentUser.uid,
            text: message,
            fileURL,
            timestamp: new Date(),
        });

        setMessage('');
        setFile(null);
    };

    // if it is loading data from the database
    if (loading) return (
        <div className="fixed top-0 left-0 w-full h-full flex justify-center items-center bg-white bg-opacity-50">
            <Loader2 className="animate-spin mr-2 text-gray-600" size={24} />
            <p className="text-lg text-gray-600">Please Wait...</p>
        </div>
    );
    if (!booking) return <p>No booking found.</p>;

    const captureQR = async () => {
        if (!qrRef.current) return;

        try {
            const canvas = await html2canvas(qrRef.current, {
                useCORS: true,
                logging: true,
            });

            const image = canvas.toDataURL("image/png");
            const link = document.createElement("a");
            link.href = image;
            link.download = `booking-${booking.id}.png`;
            link.click();
        } catch (error) {
            console.error("Error capturing screenshot:", error);
        }
    }

    return (
        <>
            <div ref={qrRef} className="max-w-lg mx-auto p-6 bg-white shadow-lg rounded-xl mt-10">
                <h2 className="text-xl font-bold text-gray-900">Booking Details</h2>
                <div className="flex flex-col items-center gap-4 mt-4">
                    <QRCode
                         id={`qr-${booking.id}`} value={`https://www.booking.ooobcs.org/bookings/${booking.id}`} size={150} />
                    <button
                        onClick={captureQR}
                        className="flex bg-black items-center gap-2 px-4 py-2 bg-gray-900 text-white text-sm rounded-full shadow hover:bg-gray-700 transition"
                    >
                        <Download size={16} /> Download QR
                    </button>
                </div>

                <div className="mt-4 space-y-3 text-sm">
                    <p><strong>Purpose:</strong> {booking?.purpose || booking?.customPurpose}</p>
                    <p><strong>Visit Type:</strong> {booking?.visitType}</p>
                    {booking?.groupMemberNames && Array.isArray(booking.groupMemberNames) && booking.groupMemberNames.length > 0 && (
                        <div>
                            <p><strong>Group Members ({booking.groupMemberNames.length}):</strong></p>
                            <ul className="ml-4 list-disc space-y-1">
                                {booking.groupMemberNames.map((name, index) => (
                                    name && (
                                        <li key={index} className="text-gray-700">{name}</li>
                                    )
                                ))}
                            </ul>
                        </div>
                    )}
                    {booking?.groupMemberNames && typeof booking.groupMemberNames === 'string' && booking.groupMemberNames && (
                        <div>
                            <p><strong>Group Members:</strong></p>
                            <p className="ml-4 text-gray-700 whitespace-pre-line">{booking.groupMemberNames}</p>
                        </div>
                    )}
                    <p><strong>Explanation:</strong> {booking?.explanation}</p>
                    <p>
                        <strong>Status:</strong>
                        <span className={`px-2 text-sm py-1 rounded-full ${booking?.status === "pending" ? "bg-[#F79009] text-white" : booking?.status === "Processing" ? 'bg-purple-300' : booking?.status === "rejected" ? 'bg-red-300' : "bg-green-500 text-white"}`}>
                            {booking?.status}
                        </span>
                    </p>
                </div>
                {booking?.visitType === "Group" &&
                 <div className='mt-5'>
                    <label className="block text-sm font-medium text-black">Group Appreciation Payment.</label>
                    <Homes />
                    <label className="block text-sm font-medium text-gray-700 ">Include Prove of Payment in Chat box Below Right</label>
                 </div>
                }

                {/* Display Files */}
                {booking?.doc?.length > 0 && (
                    <div className="mt-4">
                        <h3 className="text-lg font-semibold text-gray-800">Attached Files</h3>
                        <div className="mt-2 space-y-2">
                            {booking.doc.map((file, index) => (
                                <div key={index} className="flex items-center gap-3 bg-gray-100 p-2 rounded-lg">
                                    {file?.endsWith('.jpg') || file.endsWith('.png') || file.endsWith('.jpeg') ? (
                                        <img src={file} alt={`File ${index + 1}`} className="w-12 h-12 rounded-md object-cover" />
                                    ) : (
                                        <File className="text-gray-600" size={20} />
                                    )}
                                    <a href={file} target="_blank" rel="noopener noreferrer" className="text-blue-600 text-sm truncate">
                                        {file}
                                    </a>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                <div className="border-t my-4"></div>
                <div className="flex items-center gap-6">
                    <div className="flex items-center gap-2">
                        <div className="flex items-center justify-center w-10 h-10 rounded-full bg-gray-200">
                            <Clock className="text-gray-600" size={18} />
                        </div>
                        <span className="text-sm text-gray-600">{formattedTime || "Not assigned"}</span>
                    </div>
                    <div className="h-px w-12 bg-gray-400"></div>
                    <div className="flex items-center gap-2">
                        <div className="flex items-center justify-center w-10 h-10 rounded-full bg-gray-200">
                            <Calendar className="text-gray-600" size={18} />
                        </div>
                        <span className="text-sm text-gray-600">{formattedDate}</span>
                    </div>
                    <ChevronRight className="text-gray-600" size={18} />
                </div>
                <div className="mt-4 text-center text-sm text-gray-600">
                    {booking?.assignedData ? (
                        <p><strong>Scheduled:</strong> {formattedTime} - {formattedDate}</p>
                    ) : (
                        <p className="text-gray-500">Visit not assigned yet</p>
                    )}
                </div>
            </div>

            {/* Chat Modal */}
            {chatOpen && (
                <div className="fixed inset-0 bg-black opacity-50 z-40" onClick={() => setChatOpen(false)} />
            )}

            <div className={`fixed bottom-10 right-4 z-50 w-[350px] h-[450px] bg-white shadow-lg rounded-lg transform transition-transform ease-in-out duration-300 ${chatOpen ? 'translate-x-0' : 'translate-x-full'}`}>
                <div className="p-4 h-full flex flex-col">
                    <h3 className="text-lg font-semibold text-center">Chat</h3>
                    <div className="flex flex-col space-y-4 mt-4 overflow-y-auto flex-grow">
                        {messages?.map((msg, index) => (
                            <div key={index} className={`flex ${msg?.sender === auth.currentUser.uid ? "justify-end" : "justify-start"}`}>
                                <div className={`p-2 rounded-lg ${msg?.sender === auth.currentUser.uid ? "bg-[#000] text-white" : "bg-[#eee]"}`}>
                                    <p>{msg?.text}</p>
                                    {msg?.fileURL && (
                                        <div className="mt-2 flex items-center space-x-2">
                                            <Paperclip size={16} />
                                            <a href={msg.fileURL} target="_blank" rel="noopener noreferrer" className={`text-sm ${msg.sender === auth.currentUser.uid ? '#fff' : 'grey'}`}>
                                                View File
                                            </a>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                    {/* Message Input */}
                    <div className="mt-4 flex items-center">
                        <label htmlFor="file-upload" className="cursor-pointer p-2 bg-gray-200 rounded-full">
                            <Paperclip size={18} />
                            <input
                                id="file-upload"
                                type="file"
                                className="hidden"
                                onChange={(e) => setFile(e.target.files[0])}
                            />
                        </label>
                        <input
                            type="text"
                            className="w-full p-2 border rounded-lg ml-2"
                            placeholder="Type a message..."
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                        />
                        <button onClick={sendMessage} className="ml-2 p-2 bg-gray-800 text-white rounded-full bg-black">
                            <Send size={18} />
                        </button>
                    </div>
                </div>
            </div>

            <div className="max-w-lg mx-auto p-6 bg-white shadow-lg rounded-xl mt-10 items-center flex content-center justify-center">
                {!booking?.confirmed && (
                    booking?.status === "approved" && !confirmed && (
                        <button
                            onClick={confirmAttendance}
                            className="-mt-10 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-500"
                        >
                            <CheckCircle className="inline-block mr-2" size={16} /> Confirm Attendance
                        </button>
                    )
                )}
                {booking?.confirmed && <p className="text-green-600 -mt-12 flex items-center self-center justify-center text-center">You have confirmed your attendance.</p>}
            </div>

            {/* Floating Chat Icon */}
            <div
                className="fixed bottom-4 right-4 p-4 bg-blue-500 text-white rounded-full shadow-lg cursor-pointer md:right-4 md:bottom-4"
                onClick={() => setChatOpen(true)}
            >
                <MessageCircle size={24} color="#000"/>
            </div>
        </>
    );
};

export default ViewVisit